import { OverlayContainer } from '@angular/cdk/overlay';
import { DestroyRef, Injectable, Signal, computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { EMPTY, timer } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { ProfileFacade } from '@mp/auth/profile/data-access';
import {
  MaintenanceModeSignalService,
  injectMaintenanceStateQuery,
  maintenanceStateQueryKey,
} from '@mp/system/maintenance/data-access';
import { MaintenanceLockViewModel, MaintenanceState, MaintenanceStateInfo } from '@mp/system/maintenance/domain';
import {
  buildMaintenanceLockViewModel,
  computeServerTimeOffset,
  maintenanceTickIntervalMs,
  msUntilNextTransition,
  nextTransitionTimestamp,
} from '@mp/system/maintenance/util';
import { LoginContextService } from '@mpk/shared/data-access';

import { MaintenanceLockDialogComponent } from '../components/maintenance-lock-dialog/maintenance-lock-dialog.component';
import { MAINTENANCE_ROUTES, MAINTENANCE_TRANSITION_REFETCH_DELAY_MS } from '../injection-tokens';
import { MaintenanceLockDialogData } from '../models';

/**
 * Überwacht den Wartungszustand und sperrt die Oberfläche, solange der Server den Benutzer sperrt.
 *
 * Der Dienst ist die einzige Uhr des Wartungsmodus: sowohl die Sperre als auch das Banner rechnen mit der
 * hier korrigierten Serverzeit, damit beide nie unterschiedliche Restzeiten anzeigen.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceLockService {
  private readonly dialog = inject(MatDialog);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly maintenanceModeSignalService = inject(MaintenanceModeSignalService);
  private readonly profileFacade = inject(ProfileFacade);
  private readonly loginContextService = inject(LoginContextService);
  private readonly queryClient = inject(QueryClient);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly routes = inject(MAINTENANCE_ROUTES);
  private readonly transitionRefetchDelayMs = inject(MAINTENANCE_TRANSITION_REFETCH_DELAY_MS);

  private readonly stateQuery = injectMaintenanceStateQuery();

  /**
   * Diese Routen bleiben auch während der Wartung bedienbar.
   *
   * Die Ausnahme von der Sperre gilt je aktiver Organisation: ein Administrator, der in eine Organisation ohne
   * Ausnahme gewechselt ist, wird gesperrt und kommt nur über die Organisationsauswahl zurück.
   */
  private readonly unlockedUrls: readonly string[] = [
    this.routes.organizationSelectionUrl,
    ...(this.routes.additionalUnlockedUrls ?? []),
  ];

  /**
   * The maintenance state as reported by the server, `null` while it has not been loaded.
   */
  readonly stateInfo: Signal<MaintenanceStateInfo | null> = computed(() => this.stateQuery.data() ?? null);

  private readonly isLockedByServer: Signal<boolean> = computed(() => {
    const stateInfo = this.stateInfo();

    return stateInfo?.state === MaintenanceState.Active && !stateInfo.isExemptForCurrentUser;
  });

  private readonly requiresLock: Signal<boolean> = computed(
    () => this.isLockedByServer() || this.maintenanceModeSignalService.lockedMarker() !== null,
  );

  private readonly currentUrl: Signal<string> = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  private readonly isOnUnlockedUrl: Signal<boolean> = computed(() => this.isUnlockedUrl(this.currentUrl()));

  /**
   * Whether the application is locked, either by the server state or by a blocked request.
   *
   * Auf den freigeschalteten Routen wird die Sperre ausgesetzt, damit der Organisationswechsel bedienbar
   * bleibt; der Zustand des Servers bleibt davon unberührt.
   */
  readonly isLocked: Signal<boolean> = computed(() => this.requiresLock() && !this.isOnUnlockedUrl());

  /**
   * Solange weder ein Fenster ansteht noch eine Sperre läuft, gibt es keinen Countdown anzuzeigen. Die Uhr
   * steht dann still, statt jede Sekunde die Änderungserkennung der gesamten Anwendung anzustoßen.
   */
  private readonly needsTick: Signal<boolean> = computed(() => {
    const state = this.stateInfo()?.state;

    return (
      state === MaintenanceState.Announced ||
      state === MaintenanceState.Active ||
      this.maintenanceModeSignalService.lockedMarker() !== null
    );
  });

  // Der Takt hängt an der Nähe zur Umschaltzeit, nicht an der Uhr, die er selbst antreibt.
  private readonly tickIntervalMs: Signal<number> = computed(() =>
    maintenanceTickIntervalMs(this.stateInfo(), this.requiresLock()),
  );

  private readonly tick: Signal<number> = toSignal(
    toObservable(computed(() => (this.needsTick() ? this.tickIntervalMs() : null))).pipe(
      switchMap((intervalMs) => (intervalMs === null ? EMPTY : timer(0, intervalMs))),
      map(() => Date.now()),
    ),
    { initialValue: Date.now() },
  );

  /**
   * The current time (epoch milliseconds) corrected by the server time offset.
   */
  readonly correctedNow: Signal<number> = computed(() => this.tick() + this.serverTimeOffset());

  private readonly serverTimeOffset: Signal<number> = computed(() => {
    const stateInfo = this.stateInfo();

    return stateInfo ? computeServerTimeOffset(stateInfo.serverUtcNow, this.stateQuery.dataUpdatedAt()) : 0;
  });

  private readonly lockViewModel: Signal<MaintenanceLockViewModel> = computed(() => {
    const stateInfo = this.stateInfo();

    // Solange der Marker gesetzt ist, ist er mindestens so frisch wie stateInfo: er entsteht aus dem
    // 503-Body desselben prozessweiten Cache, den auch die letzte 200-Antwort bediente, und wird erst
    // aufgehoben, wenn eine neuere 200-Antwort eintrifft (synchronizeLock). Ein verkürztes/verlängertes
    // Fenster zeigte hier sonst bis zur nächsten Zustandsantwort noch das alte Ende.
    return buildMaintenanceLockViewModel({
      announcementText: stateInfo?.announcementText ?? null,
      endUtc: this.maintenanceModeSignalService.lockedMarker()?.endUtc ?? stateInfo?.endUtc ?? null,
      canSwitchOrganization: (this.profileFacade.profile()?.availableOrganizations.length ?? 0) > 1,
      correctedNow: this.correctedNow(),
    });
  });

  private dialogRef: MatDialogRef<MaintenanceLockDialogComponent, void> | null = null;
  private handledTransitionUtc: string | null = null;
  private readonly inertedElements = new Set<Element>();

  constructor() {
    effect(() => this.refetchOnBlockedRequest());
    effect(() => this.refetchOnElapsedTransition());
    effect(() => this.synchronizeLock());
  }

  /**
   * Ein markierter 503 sperrt die Oberfläche sofort; der Server bestätigt oder widerlegt die Sperre mit der
   * angeforderten Antwort.
   */
  private refetchOnBlockedRequest(): void {
    if (this.maintenanceModeSignalService.lockedMarker() === null) {
      return;
    }

    void this.queryClient.invalidateQueries({ queryKey: maintenanceStateQueryKey });
  }

  /**
   * Erreicht die korrigierte Uhr die bekannte Umschaltzeit - den Beginn eines angekündigten, das Ende eines
   * laufenden Fensters -, wird der Zustand neu geholt.
   *
   * Ohne dieses Nachfragen hinkt der Wechsel um ein Pollintervall nach: das Banner der Ankündigung
   * verschwindet mit dem abgelaufenen Countdown, der Hinweis auf die laufende Wartung erscheint aber erst mit
   * der nächsten Antwort. Umschalten darf ausschließlich der Server - hier wird nur gefragt, denn das Fenster
   * kann inzwischen verlängert oder verschoben worden sein. Der Merker begrenzt das auf eine Anfrage pro
   * Umschaltzeit statt einer pro Takt.
   *
   * Gefragt wird um die je Sitzung gezogene Verzögerung versetzt: das Fensterende ist für alle gesperrten
   * Sitzungen dieselbe Sekunde, und ohne Streuung träfen deren Anfragen und die anschließenden Neustarts der
   * Anwendung alle gleichzeitig ein - genau dann, wenn der Host gerade aus der Wartung zurückkommt.
   */
  private refetchOnElapsedTransition(): void {
    const stateInfo = this.stateInfo();

    if (stateInfo === null) {
      return;
    }

    const transitionUtc = nextTransitionTimestamp(stateInfo);

    // Ohne offengelegtes Fenster gibt es keine Umschaltzeit: der Merker gilt wieder für das nächste Fenster.
    if (transitionUtc === null) {
      this.handledTransitionUtc = null;

      return;
    }

    const untilTransition = msUntilNextTransition(stateInfo, this.correctedNow());

    if (
      untilTransition === null ||
      untilTransition > -this.transitionRefetchDelayMs ||
      this.handledTransitionUtc === transitionUtc
    ) {
      return;
    }

    this.handledTransitionUtc = transitionUtc;

    void this.queryClient.invalidateQueries({ queryKey: maintenanceStateQueryKey });
  }

  private synchronizeLock(): void {
    const marker = this.maintenanceModeSignalService.lockedMarker();

    /**
     * Nur eine Antwort, die nicht VOR dem gesperrten Request geholt wurde, hebt dessen Marker auf - sonst
     * würde die zuletzt zwischengespeicherte Antwort die Sofortsperre unmittelbar wieder aufheben.
     *
     * Verglichen wird mit `>=`: beide Zeitstempel sind Millisekunden aus `Date.now()`. Fallen der 503 und die
     * nächste Zustandsantwort in dieselbe Millisekunde, wäre die Antwort bei `>` nicht "frisch" genug und der
     * Benutzer bliebe bis zum nächsten Poll auf der Sperrmeldung, obwohl der Server ihn nicht mehr sperrt.
     * Wirklich veraltete Antworten (Zeitstempel kleiner als der des Markers) fallen weiterhin durch.
     */
    const hasFreshServerAnswer =
      this.stateInfo() !== null &&
      !this.stateQuery.isFetching() &&
      (marker === null || this.stateQuery.dataUpdatedAt() >= marker.receivedAt);

    if (hasFreshServerAnswer && !this.isLockedByServer()) {
      this.maintenanceModeSignalService.clearLocked();
    }

    if (this.isLocked()) {
      this.openLockDialog();

      return;
    }

    /**
     * Auf einer freigeschalteten Route wird die Sperre nur ausgesetzt: der Dialog geht zu, neu geladen wird
     * aber nicht - der Benutzer soll dort ja gerade seine Organisation wechseln.
     */
    if (this.isOnUnlockedUrl()) {
      this.closeLockDialog();

      return;
    }

    /**
     * `dialogRef` ist der Beleg dafür, dass gesperrt war: der Dialog lässt sich nicht wegklicken, er steht
     * genau so lange wie die Sperre.
     */
    if (!hasFreshServerAnswer || this.dialogRef === null) {
      return;
    }

    /**
     * Neu laden statt nur den Dialog schließen: während der Sperre sind Requests mit 503 gescheitert, die
     * Stores der offenen Seite sind also unvollständig.
     */
    this.closeLockDialog();
    this.reloadApplication();
  }

  private openLockDialog(): void {
    if (this.dialogRef !== null) {
      return;
    }

    const data: MaintenanceLockDialogData = {
      viewModel: this.lockViewModel,
      /**
       * Der Organisationswechsel läuft rein clientseitig und über freigeschaltete Endpunkte - ein
       * Administrator, der in eine Organisation ohne Ausnahme gewechselt ist, kommt so immer zurück.
       */
      switchOrganization: () => this.assignLocation(this.routes.organizationSelectionUrl),
      logout: () => this.loginContextService.scheduleLogout(),
    };

    this.dialogRef = this.dialog.open<MaintenanceLockDialogComponent, MaintenanceLockDialogData, void>(
      MaintenanceLockDialogComponent,
      {
        data,
        // Die Sperre darf sich nicht wegklicken lassen: kein ESC, kein Backdrop-Klick.
        disableClose: true,
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
        maxHeight: '100vh',
        /**
         * Der Fokus muss in den Dialog wandern, sonst greift die Fokusfalle der CDK nicht und die gesperrte
         * Anwendung bleibt mit der Tabulatortaste erreichbar. Angesprungen wird die Überschrift und nicht die
         * erste Schaltfläche, damit ein Bildschirmleser die Sperre vorliest, ohne "Abmelden" vorzuwählen.
         */
        autoFocus: 'first-heading',
        // Als modaler Dialog angekündigt: der Bildschirmleser verlässt die Sperre nicht in den Hintergrund.
        ariaModal: true,
        ariaLabel: 'Wartungsmodus',
      },
    );

    this.setBackgroundInert();

    this.dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.dialogRef = null;
          // Auch ein von außen geschlossener Dialog darf den Hintergrund nicht unbedienbar zurücklassen.
          this.releaseBackgroundInert();
        },
      });
  }

  private closeLockDialog(): void {
    if (this.dialogRef === null) {
      return;
    }

    this.dialogRef.close();
    this.dialogRef = null;
    this.releaseBackgroundInert();
  }

  /**
   * Nimmt die Anwendung hinter der Sperre vollständig aus der Bedienung.
   *
   * Die CDK blendet den Hintergrund nur mit `aria-hidden` aus - fokussierbar bleibt er damit trotzdem, und
   * `aria-hidden` über fokussierbaren Inhalten führt Bildschirmleser in die Irre. Die Fokusfalle des Dialogs
   * greift erst, wenn der Fokus schon im Dialog liegt: kommt er von außen (Adressleiste, Browserleiste),
   * landet der erste Tabulatorsprung davor, also im Hintergrund. Deshalb wird der Hintergrund zusätzlich
   * `inert` gesetzt - nicht fokussierbar, nicht anklickbar, nicht vorgelesen.
   */
  private setBackgroundInert(): void {
    const containerElement = this.overlayContainer.getContainerElement();
    const parentElement = containerElement.parentElement;

    if (parentElement === null) {
      return;
    }

    for (const sibling of Array.from(parentElement.children)) {
      // Ein schon von außen stillgelegtes Element gehört nicht uns: es bleibt es auch nach der Sperre.
      if (sibling === containerElement || !isInertableElement(sibling) || sibling.hasAttribute('inert')) {
        continue;
      }

      this.inertedElements.add(sibling);
      sibling.setAttribute('inert', '');
    }
  }

  private releaseBackgroundInert(): void {
    for (const element of this.inertedElements) {
      element.removeAttribute('inert');
    }

    this.inertedElements.clear();
  }

  private isUnlockedUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];

    return this.unlockedUrls.some((unlockedUrl) => path === unlockedUrl || path.startsWith(`${unlockedUrl}/`));
  }

  protected assignLocation(url: string): void {
    self.location.assign(url);
  }

  protected reloadApplication(): void {
    self.location.reload();
  }
}

/**
 * Die Meldungsbereiche der CDK (`aria-live`) müssen vorgelesen werden können - sie bleiben unangetastet.
 */
function isInertableElement(element: Element): boolean {
  return !element.hasAttribute('aria-live');
}
